import { WorkshopHomeApi } from './workshopHomeApi';

const NOTE_FILE_ALIASES = [
  'nota_remessa_file_name',
  'notaRemessaFileName',
  'nota_remessa',
  'notaRemessa',
];

const WORKSHOP_HOME_STATUSES = [
  'aguardando equipamento',
  'equipamento em analise',
  'pendente de aprovação',
  'autorizar manutencao',
  'aprovado para manutencao',
  'aguardando inspeção e laudo',
  'pronto para retirada',
  'enviado',
];

function normalizeStatus(value) {
  return String(value || '').trim().toLowerCase();
}

function createDefaultCounters() {
  return WORKSHOP_HOME_STATUSES.reduce((acc, status) => {
    acc[status] = 0;
    return acc;
  }, {});
}

function buildCountersFromApi(rawCounters = {}) {
  const counters = createDefaultCounters();

  WORKSHOP_HOME_STATUSES.forEach((status) => {
    const parsed = Number(rawCounters[status]);
    counters[status] = Number.isFinite(parsed) ? parsed : 0;
  });

  return counters;
}

function parseClienteFromItens(itens) {
  if (!itens) return { nome: '', cnpj: '' };
  try {
    const parsed = typeof itens === 'string' ? JSON.parse(itens) : itens;
    const cliente = parsed?.cliente || {};
    return {
      nome: cliente.nome || '',
      cnpj: cliente.cnpj || '',
    };
  } catch {
    return { nome: '', cnpj: '' };
  }
}

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj || {}, key);
}

function hasNotaRemessaField(quotation = {}) {
  if (NOTE_FILE_ALIASES.some((alias) => hasOwn(quotation, alias))) {
    return true;
  }

  if (hasOwn(quotation, 'nota_remessa_file')) {
    return true;
  }

  if (Array.isArray(quotation.serviceFiles)) {
    return quotation.serviceFiles.some((file) => {
      const type = normalizeStatus(file?.type || file?.fileType);
      return type === 'nota_remessa' || type === 'nota remessa';
    });
  }

  return false;
}

function extractNotaRemessaFileName(quotation = {}) {
  const alias = NOTE_FILE_ALIASES.find((candidate) => hasOwn(quotation, candidate));

  if (alias) {
    return quotation[alias];
  }

  const nestedNota = quotation.nota_remessa_file;
  if (nestedNota && typeof nestedNota === 'object') {
    return nestedNota.file_name || nestedNota.fileName || nestedNota.nome || null;
  }

  if (Array.isArray(quotation.serviceFiles)) {
    const notaRemessa = quotation.serviceFiles.find((file) => {
      const type = normalizeStatus(file?.type || file?.fileType);
      return type === 'nota_remessa' || type === 'nota remessa';
    });

    if (notaRemessa) {
      return notaRemessa.fileName || notaRemessa.file_name || notaRemessa.nome || null;
    }
  }

  return null;
}

function collectServiceFileNames(quotation = {}) {
  const candidates = [];
  const directNames = [
    quotation.service_file_names,
    quotation.serviceFileNames,
  ];
  const serviceFilesGroups = [
    quotation.serviceFiles,
    quotation.service_files,
  ];

  directNames.forEach((group) => {
    if (!Array.isArray(group)) return;
    group.forEach((name) => {
      if (typeof name === 'string' && name.trim()) {
        candidates.push(name.trim());
      }
    });
  });

  serviceFilesGroups.forEach((group) => {
    if (!Array.isArray(group)) return;

    group.forEach((file) => {
      if (typeof file === 'string' && file.trim()) {
        candidates.push(file.trim());
        return;
      }

      if (file && typeof file === 'object') {
        const type = String(file.type || file.fileType || '').toLowerCase();
        if (type !== 'nota_remessa' && type !== 'nota remessa') return;
        const name = file.fileName || file.file_name || file.nome || file.name;
        if (typeof name === 'string' && name.trim()) {
          candidates.push(name.trim());
        }
      }
    });
  });

  const fallbackName = extractNotaRemessaFileName(quotation);
  if (typeof fallbackName === 'string' && fallbackName.trim()) {
    candidates.push(fallbackName.trim());
  }

  return Array.from(new Set(candidates));
}

function formatDashboardData(data) {
  const rawQuotations = data.cotacoes || data.orders || data;
  const rawCounters = data.contadores || data.counters || {};

  const quotations = Array.isArray(rawQuotations)
    ? rawQuotations.map((item) => {
      const clienteFromItens = parseClienteFromItens(item.itens);
      return {
        id: item.id || item.codigo,
        tipo: item.tipo || item.type,
        status: normalizeStatus(item.status),
        data: item.data || item.created_at,
        cnpj: item.cnpj || clienteFromItens.cnpj,
        cliente: item.cliente || clienteFromItens.nome,
        user_id: item.user_id,
        assumed_by_workshop_user_id: item.assumed_by_workshop_user_id || null,
        assumed_by_workshop_user_name: item.assumed_by_workshop_user_name || null,
        assumed_by_workshop_at: item.assumed_by_workshop_at || null,
        notaRemessaFileName: extractNotaRemessaFileName(item),
        serviceFileNames: collectServiceFileNames(item),
        hasNotaRemessaField: hasNotaRemessaField(item),
      };
    })
    : [];

  const filteredQuotes = quotations.filter((item) => WORKSHOP_HOME_STATUSES.includes(item.status));
  const counters = buildCountersFromApi(rawCounters);

  if (!data.contadores && !data.counters) {
    filteredQuotes.forEach((item) => {
      if (Object.prototype.hasOwnProperty.call(counters, item.status)) {
        counters[item.status] += 1;
      }
    });
  }

  return {
    HOME_Cotacoes: filteredQuotes,
    contadores: counters,
  };
}

function formatEquipmentData(equipment) {
  return {
    id: equipment.id,
    nome: equipment.nome,
    marca: equipment.marca,
    modelo: equipment.modelo,
    status: equipment.status,
    company_id: equipment.company_id,
  };
}

function formatarData(dataString) {
  if (!dataString) return '-';
  try {
    return new Date(dataString).toLocaleDateString('pt-BR');
  } catch {
    return '-';
  }
}

function getDashboardData() {
  return WorkshopHomeApi.getDashboardData()
    .then((response) => formatDashboardData(response.data))
    .catch((error) => {
      throw new Error(error.response?.data?.error || 'Erro ao carregar dashboard');
    });
}

function getStatusCounters() {
  return WorkshopHomeApi.getStatusCounters()
    .then((response) => response.data.contadores || response.data)
    .catch((error) => {
      throw new Error(error.response?.data?.error || 'Erro ao carregar contadores');
    });
}

function getEquipmentById(equipmentId) {
  return WorkshopHomeApi.getEquipmentById(equipmentId)
    .then((response) => formatEquipmentData(response.data))
    .catch((error) => {
      throw new Error(error.response?.data?.error || 'Equipamento não encontrado');
    });
}

function getDataByStatus(status) {
  return WorkshopHomeApi.getDataByStatus(status)
    .then((response) => {
      const cotacoes = response.data.cotacoes || response.data;
      if (!Array.isArray(cotacoes)) return [];
      return cotacoes.map((item) => {
        const clienteFromItens = parseClienteFromItens(item.itens);
        return {
          ...item,
          status: normalizeStatus(item.status),
          cnpj: item.cnpj || clienteFromItens.cnpj,
          cliente: item.cliente || clienteFromItens.nome,
          assumed_by_workshop_user_id: item.assumed_by_workshop_user_id || null,
          assumed_by_workshop_user_name: item.assumed_by_workshop_user_name || null,
          assumed_by_workshop_at: item.assumed_by_workshop_at || null,
          notaRemessaFileName: extractNotaRemessaFileName(item),
          serviceFileNames: collectServiceFileNames(item),
          hasNotaRemessaField: hasNotaRemessaField(item),
        };
      });
    })
    .catch((error) => {
      throw new Error(error.response?.data?.error || 'Erro ao filtrar por status');
    });
}

function refreshDashboard() {
  return WorkshopHomeApi.refreshDashboard()
    .then((response) => formatDashboardData(response.data))
    .catch((error) => {
      throw new Error(error.response?.data?.error || 'Erro ao atualizar dashboard');
    });
}

function updateEstimateStatus(id, status) {
  return WorkshopHomeApi.updateEstimateStatus(id, status)
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(error.response?.data?.error || 'Erro ao atualizar status');
    });
}

function assumeQuotation(quotationId, user) {
  const payload = {
    quotationId: quotationId,
    userId: user.id || user.user_id,
    userName: user.name || user.nome || user.userName,
  };
  return WorkshopHomeApi.assumeQuotation(payload)
    .then((response) => ({
      success: true,
      data: response.data,
    }))
    .catch((error) => {
      throw new Error(error.response?.data?.error || 'Erro ao assumir cotação');
    });
}

function unassignQuotation(quotationId, user) {
  const payload = {
    quotationId: quotationId,
    userId: user.id || user.user_id,
  };
  return WorkshopHomeApi.unassignQuotation(payload)
    .then((response) => ({
      success: true,
      data: response.data,
    }))
    .catch((error) => {
      throw new Error(error.response?.data?.error || 'Erro ao devolver cotação');
    });
}

export const WorkshopHomeService = {
  WORKSHOP_HOME_STATUSES,
  normalizeStatus,
  getDashboardData,
  getStatusCounters,
  getEquipmentById,
  getDataByStatus,
  refreshDashboard,
  updateEstimateStatus,
  formatarData,
  assumeQuotation,
  unassignQuotation,
};
